import axios from 'axios';

import createUrlWithParams from 'helpers/createUrlWithParams';

import {
  parseActionLibraryInstanceExportParamsForBE,
  parseActionLibraryInstanceListForFE,
  parseAllActionLibraryInstancesForFE,
  parseBulkCreateActionLibraryInstanceForBE,
  parseCompleteActionLibraryInstanceForBE,
  parseDelegateActionLibraryInstanceForBE,
  parseReopenInstanceForBE,
  parseReturnToOwnerActionLibraryInstanceForBE,
  parseInstanceActivityLogListForFE,
  parseInstanceCommentCreateForBE,
  parseInstanceCommentForFE,
  parseInstanceCommentListForFE,
  parseInstanceDashboardMetricsForFE,
  parseUpdateActionLibraryInstanceForBE,
  resolveActionLibraryInstanceExportFilename,
} from './actionLibraryInstance.parsers';
import type {
  ActionLibraryInstanceAPI,
  ActionLibraryInstanceDTO,
  CreateBulkActionLibraryInstancesResponse,
  InstanceActivityLogEntryDTO,
  InstanceCommentDTO,
} from './actionLibraryInstance.types';

const endpoint = 'api/v1/asset-actions/';
const personalActionsEndpoint = 'api/v1/asset-actions/personal-actions/';

type PaginatedListResponse = {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: ActionLibraryInstanceDTO[];
};

type ListParams = {
  limit?: number;
  offset?: number;
};

const parseListResponse = (data: unknown, params: ListParams = {}) => {
  if (Array.isArray(data)) {
    const limit = params.limit ?? data.length;
    const offset = params.offset ?? 0;
    const paginatedData = data.slice(offset, offset + limit);

    return {
      count: data.length,
      next: offset + limit < data.length ? 'client-side-next-page' : null,
      previous: null,
      results: parseAllActionLibraryInstancesForFE(paginatedData as ActionLibraryInstanceDTO[]),
    };
  }
  const paginated = data as PaginatedListResponse;
  return {
    count: paginated.count ?? 0,
    next: paginated.next ?? null,
    previous: paginated.previous ?? null,
    results: parseAllActionLibraryInstancesForFE(paginated.results ?? []),
  };
};

export const actionLibraryInstancesApi: ActionLibraryInstanceAPI = {
  getAllActionLibraryInstances: async params => {
    const { data } = await axios.get(createUrlWithParams(endpoint, params || {}));
    return { data: parseListResponse(data, params) };
  },

  exportActionLibraryInstances: async params => {
    const url = createUrlWithParams(`${endpoint}export/`, parseActionLibraryInstanceExportParamsForBE(params));
    const response = await axios.get<Blob>(url, { responseType: 'blob' });
    const disposition =
      (response.headers['content-disposition'] as string | undefined) ?? (response.headers['Content-Disposition'] as string | undefined);
    const rawContentType =
      (response.headers['content-type'] as string | undefined) ?? (response.headers['Content-Type'] as string | undefined);
    return {
      data: {
        blob: response.data,
        contentType: rawContentType ?? null,
        filename: resolveActionLibraryInstanceExportFilename(disposition, params.export_format),
      },
    };
  },

  getPersonalActionLibraryInstances: async params => {
    const { data } = await axios.get(createUrlWithParams(personalActionsEndpoint, params || {}));
    return { data: parseListResponse(data, params) };
  },

  getSingleActionLibraryInstance: async id => {
    const { data } = await axios.get(`${endpoint}${id}/`);
    return { data: parseActionLibraryInstanceListForFE(data) };
  },

  deleteActionLibraryInstance: async id => {
    await axios.delete(`${endpoint}${id}/`);
  },

  createBulkActionLibraryInstances: async data => {
    const response = await axios.post(`${endpoint}create_bulk/`, parseBulkCreateActionLibraryInstanceForBE(data));
    return {
      data: response.data,
      status: response.status,
      headers: response.headers as unknown as Record<string, string>,
    } as CreateBulkActionLibraryInstancesResponse;
  },

  createBulkPrecheck: async data => {
    const payload = { template_id: data.templateId, asset_ids: data.assetIds };
    const { data: responseData } = await axios.post(`${endpoint}create-bulk-precheck/`, payload);
    const dto = responseData as { template_id: number; asset_ids: number[]; already_active_asset_ids?: number[] };
    return {
      data: {
        templateId: dto.template_id,
        assetIds: dto.asset_ids,
        alreadyActiveAssetIds: dto.already_active_asset_ids ?? [],
      },
    };
  },
  updateActionLibraryInstance: async (id, data) => {
    const { data: responseData } = await axios.patch(`${endpoint}${id}/`, parseUpdateActionLibraryInstanceForBE(data));
    return { data: parseActionLibraryInstanceListForFE(responseData) };
  },

  delegateActionLibraryInstance: async (id, data) => {
    const { data: responseData } = await axios.patch(`${endpoint}${id}/`, parseDelegateActionLibraryInstanceForBE(data));
    return { data: parseActionLibraryInstanceListForFE(responseData) };
  },

  completeActionLibraryInstance: async (id, data) => {
    const { data: responseData } = await axios.post(`${endpoint}${id}/complete/`, parseCompleteActionLibraryInstanceForBE(data));
    return { data: parseActionLibraryInstanceListForFE(responseData) };
  },

  reopenActionLibraryInstance: async (id, data) => {
    const { data: responseData } = await axios.post(`${endpoint}${id}/reopen/`, parseReopenInstanceForBE(data));
    return { data: parseActionLibraryInstanceListForFE(responseData) };
  },

  returnToOwnerActionLibraryInstance: async (id, data) => {
    const { data: responseData } = await axios.post(
      `${endpoint}${id}/return-to-owner/`,
      parseReturnToOwnerActionLibraryInstanceForBE(data),
    );
    return { data: parseActionLibraryInstanceListForFE(responseData) };
  },

  getActionLibraryInstanceMetrics: async params => {
    const { data } = await axios.get(createUrlWithParams(`${endpoint}metrics/`, params || {}));
    return { data: parseInstanceDashboardMetricsForFE(data) };
  },

  getInstanceStatusLogs: async (id, params) => {
    const { data } = await axios.get(createUrlWithParams(`${endpoint}${id}/status-logs/`, params || {}));
    const rawResults = Array.isArray(data) ? data : data.results || [];
    const results = rawResults as InstanceActivityLogEntryDTO[];
    return {
      data: {
        count: Array.isArray(data) ? data.length : data.count ?? results.length,
        next: Array.isArray(data) ? null : data.next ?? null,
        previous: Array.isArray(data) ? null : data.previous ?? null,
        results: parseInstanceActivityLogListForFE(results),
      },
    };
  },

  getInstanceComments: async (id, params) => {
    const { data } = await axios.get(createUrlWithParams(`${endpoint}${id}/comments/`, params || {}));
    const rawResults = Array.isArray(data) ? data : data.results || [];
    const results = rawResults as InstanceCommentDTO[];
    return {
      data: {
        count: Array.isArray(data) ? data.length : data.count ?? results.length,
        next: Array.isArray(data) ? null : data.next ?? null,
        previous: Array.isArray(data) ? null : data.previous ?? null,
        results: parseInstanceCommentListForFE(results),
      },
    };
  },

  createInstanceComment: async (id, payload) => {
    const { data } = await axios.post(`${endpoint}${id}/comments/`, parseInstanceCommentCreateForBE(payload));
    return { data: parseInstanceCommentForFE(data as InstanceCommentDTO) };
  },
};

export default actionLibraryInstancesApi;
